import React, { useCallback, useEffect, useState } from 'react';
import {
  SparklesIcon,
  TrashIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ClockIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import deletePromotion from '../api/deletePromotion';
import getPromotions from '../api/getPromotions';
import { ConfirmDialog } from '../components/ChatList';

const STATUSES = [
  { value: '', label: 'Все' },
  { value: 'active', label: 'Активные' },
  { value: 'pending', label: 'Ожидают' },
  { value: 'expired', label: 'Завершённые' },
];

const STATUS_BADGE = {
  active: { label: 'Активна', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  pending: { label: 'Ожидает', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  expired: { label: 'Завершена', cls: 'bg-gray-500/15 text-text-muted border-gray-500/30' },
};

const PACKAGES = {
  minimal: { label: 'Минимальный', cls: 'from-amber-500 to-orange-500' },
  optimal: { label: 'Оптимальный', cls: 'from-indigo-500 to-violet-500' },
  maximal: { label: 'Максимальный', cls: 'from-pink-500 to-rose-500' },
};

const ANN_STATUS = {
  active: 'text-emerald-500',
  pending: 'text-amber-500',
  rejected: 'text-rose-500',
  inactive: 'text-text-muted',
};

const PAGE_SIZE = 15;
const fmt = (n) => Number(n || 0).toLocaleString('ru-RU');
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const timeLeft = (days) => {
  if (days === null || days === undefined) return null;
  if (days <= 0) return 'срок истёк';
  if (days < 1) return `${Math.max(1, Math.round(days * 24))} ч`;
  return `${Math.floor(days)} дн`;
};

const idInputCls =
  'h-9 w-full rounded-lg border border-border-primary bg-bg-primary pl-8 pr-3 text-sm text-text-primary ' +
  'placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30';

const PromotionManagement = () => {
  const [status, setStatus] = useState('');
  const [announcementId, setAnnouncementId] = useState('');
  const [userId, setUserId] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 0 });
  const [counts, setCounts] = useState({});
  const [state, setState] = useState('loading');
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (!toast || toast.type === 'error') return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    setState((s) => (s === 'ready' ? 'ready' : 'loading'));
    try {
      const data = await getPromotions({
        page,
        limit: PAGE_SIZE,
        status,
        announcement_id: announcementId,
        user_id: userId,
      });
      setItems(data.items || []);
      setMeta({ total: data.total || 0, pages: data.pages || 0 });
      setState('ready');
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setState('error');
    }
  }, [page, status, announcementId, userId]);

  // ID-фильтры — с задержкой, чтобы не слать запрос на каждую цифру
  useEffect(() => {
    const t = setTimeout(load, announcementId || userId ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, announcementId, userId]);

  const loadCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        STATUSES.map((s) => getPromotions({ page: 1, limit: 1, status: s.value }).then((d) => [s.value, d.total || 0]))
      );
      setCounts(Object.fromEntries(results));
    } catch (err) {
      console.error('Error fetching promotion counts:', err);
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setAnnouncementId('');
    setUserId('');
    setStatus('');
    setPage(1);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePromotion(confirm.id);
      setToast({
        type: 'success',
        text:
          confirm.status === 'active'
            ? `Продвижение #${confirm.id} остановлено, объявление #${confirm.announcement?.id} больше не VIP`
            : `Продвижение #${confirm.id} удалено`,
      });
      setConfirm(null);
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
      else load();
      loadCounts();
    } catch (err) {
      setDeleteError(/403/.test(err.message) ? 'Удалять продвижения может только администратор.' : err.message);
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = Boolean(status || announcementId || userId);
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, meta.total);

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">VIP-продвижение</h2>
          <p className="mt-0.5 text-sm text-text-secondary">Пакеты просмотров, купленные пользователями для своих объявлений</p>
        </div>
        <button
          type="button"
          onClick={() => {
            load();
            loadCounts();
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
        >
          <ArrowPathIcon className={`h-4 w-4 ${state === 'loading' ? 'animate-spin' : ''}`} />
          Обновить
        </button>
      </div>

      <div className="flex gap-3 rounded-block border border-sky-500/30 bg-sky-500/10 px-4 py-3">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
        <div className="text-sm text-text-secondary">
          <p className="font-semibold text-text-primary">Продажа пакетов закрыта</p>
          <p className="mt-0.5">
            С тарифной реформы (сентябрь 2026) новые пакеты просмотров не продаются и не выдаются — вместо них пользователи тратят VIP-часы
            из тарифа. Ранее купленные продвижения работают до окончания: здесь их можно отслеживать и при необходимости остановить.
          </p>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STATUSES.map((s) => (
          <button
            key={s.value || 'all'}
            type="button"
            onClick={() => changeFilter(setStatus)(s.value)}
            className={`rounded-block border bg-bg-card p-4 text-left shadow-sm transition-colors ${
              status === s.value ? 'border-[#4F45E5] ring-1 ring-[#4F45E5]/40' : 'border-border-primary hover:bg-bg-tertiary/60'
            }`}
          >
            <p className="text-xs font-medium text-text-secondary">{s.value ? s.label : 'Всего продвижений'}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-text-primary">{counts[s.value] === undefined ? '—' : fmt(counts[s.value])}</p>
          </button>
        ))}
      </div>

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            toast.type === 'error' ? 'border-rose-500/30 bg-rose-500/10 text-rose-500' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
          }`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-border-primary p-3 md:flex-row md:items-center">
          <div className="flex rounded-lg border border-border-primary bg-bg-primary p-0.5">
            {STATUSES.map((s) => (
              <button
                key={s.value || 'all'}
                type="button"
                onClick={() => changeFilter(setStatus)(s.value)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  status === s.value ? 'bg-bg-card text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex flex-1 gap-2 md:justify-end">
            <label className="relative md:w-44">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">#</span>
              <input
                type="text"
                inputMode="numeric"
                value={announcementId}
                onChange={(e) => changeFilter(setAnnouncementId)(e.target.value.replace(/\D/g, ''))}
                placeholder="ID объявления"
                aria-label="ID объявления"
                className={idInputCls}
              />
            </label>
            <label className="relative md:w-44">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">#</span>
              <input
                type="text"
                inputMode="numeric"
                value={userId}
                onChange={(e) => changeFilter(setUserId)(e.target.value.replace(/\D/g, ''))}
                placeholder="ID пользователя"
                aria-label="ID пользователя"
                className={idInputCls}
              />
            </label>
            {hasFilters && (
              <button
                type="button"
                onClick={resetFilters}
                title="Сбросить фильтры"
                aria-label="Сбросить фильтры"
                className="rounded-lg border border-border-primary px-2 text-text-secondary hover:bg-bg-tertiary"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {state === 'error' ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
            <p className="text-sm text-text-secondary">Не удалось загрузить продвижения</p>
            <button type="button" onClick={load} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
              Повторить
            </button>
          </div>
        ) : state === 'loading' && items.length === 0 ? (
          <ul className="divide-y divide-border-primary">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="flex animate-pulse items-center gap-4 px-4 py-4">
                <div className="h-6 w-24 rounded bg-bg-tertiary" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/2 rounded bg-bg-tertiary" />
                  <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
                </div>
                <div className="h-2 w-32 rounded bg-bg-tertiary" />
              </li>
            ))}
          </ul>
        ) : items.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <SparklesIcon className="mx-auto mb-3 h-12 w-12 text-text-muted" />
            <p className="text-sm font-medium text-text-primary">Продвижений не найдено</p>
            {hasFilters && (
              <button type="button" onClick={resetFilters} className="mt-3 text-sm font-medium text-[#7C3AED] hover:underline dark:text-indigo-300">
                Сбросить фильтры
              </button>
            )}
          </div>
        ) : (
          <ul className={`divide-y divide-border-primary ${state === 'loading' ? 'opacity-60' : ''}`}>
            {items.map((p) => {
              const pkg = PACKAGES[p.package_type] || { label: p.package_type || '—', cls: 'from-gray-500 to-gray-600' };
              const st = STATUS_BADGE[p.status] || { label: p.status, cls: 'border-border-primary text-text-muted' };
              const progress = Math.min(Number(p.progress_percentage || 0), 100);
              const left = p.status === 'active' ? timeLeft(p.remaining_time_days) : null;
              return (
                <li key={p.id} className="group grid grid-cols-1 gap-3 px-4 py-3.5 hover:bg-bg-tertiary/40 lg:grid-cols-[9rem_minmax(0,1.4fr)_minmax(0,1fr)_12rem_8rem_2.5rem] lg:items-center">
                  <div className="flex items-center gap-2 lg:flex-col lg:items-start lg:gap-1">
                    <span className={`inline-flex rounded-full bg-gradient-to-r px-2.5 py-0.5 text-xs font-semibold text-white ${pkg.cls}`}>{pkg.label}</span>
                    <span className="font-mono text-[11px] text-text-muted">промо #{p.id}</span>
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-text-primary" title={p.announcement?.title}>
                      {p.announcement?.title || 'Объявление удалено'}
                    </p>
                    <p className="text-xs text-text-muted">
                      <span className="font-mono">#{p.announcement?.id ?? '—'}</span>
                      {p.announcement?.status && (
                        <span className={`ml-1.5 ${ANN_STATUS[p.announcement.status] || ''}`}>· {p.announcement.status}</span>
                      )}
                    </p>
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-sm text-text-primary">{p.user?.name || 'Без имени'}</p>
                    <p className="truncate text-xs text-text-muted">
                      <span className="font-mono">#{p.user?.id ?? '—'}</span>
                      {(p.user?.phone_number || p.user?.email) && ` · ${p.user.phone_number || p.user.email}`}
                    </p>
                  </div>

                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="inline-flex items-center gap-1 text-text-secondary">
                        <EyeIcon className="h-3.5 w-3.5" />
                        <span className="tabular-nums text-text-primary">{fmt(p.views_used ?? 0)}</span> / {fmt(p.max_views)}
                      </span>
                      <span className="tabular-nums text-text-muted">{progress}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-bg-tertiary">
                      <div
                        className={`h-full rounded-full ${p.status === 'active' ? 'bg-accent-grad' : 'bg-gray-400/60'}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 lg:flex-col lg:items-start lg:gap-1">
                    <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-text-muted" title={`Создано ${fmtDate(p.created_at)}`}>
                      <ClockIcon className="h-3.5 w-3.5" />
                      {left ? `осталось ${left}` : p.expires_at ? `до ${fmtDate(p.expires_at)}` : fmtDate(p.created_at)}
                    </span>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(null);
                        setConfirm(p);
                      }}
                      title={p.status === 'active' ? 'Остановить продвижение' : 'Удалить запись'}
                      aria-label="Удалить продвижение"
                      className="rounded-lg p-2 text-text-muted opacity-60 transition-opacity hover:bg-rose-500/10 hover:text-rose-500 focus:opacity-100 group-hover:opacity-100"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {state !== 'error' && meta.total > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-border-primary px-4 py-3">
            <span className="text-xs text-text-secondary">
              {fmt(from)}–{fmt(to)} из {fmt(meta.total)}
            </span>
            {meta.pages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((x) => Math.max(1, x - 1))}
                  disabled={page <= 1}
                  aria-label="Предыдущая страница"
                  className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="min-w-[4.5rem] text-center text-sm font-medium text-text-primary">
                  {page} / {meta.pages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((x) => x + 1)}
                  disabled={page >= meta.pages}
                  aria-label="Следующая страница"
                  className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {confirm && (
        <ConfirmDialog
          title={confirm.status === 'active' ? `Остановить продвижение #${confirm.id}?` : `Удалить продвижение #${confirm.id}?`}
          text={
            confirm.status === 'active'
              ? `Объявление «${confirm.announcement?.title || `#${confirm.announcement?.id}`}» сразу перестанет быть VIP. Оставшиеся ${fmt(
                  confirm.remaining_views
                )} просмотров сгорят, деньги пользователю автоматически не возвращаются. Действие необратимо.`
              : 'Запись о продвижении будет удалена без возможности восстановления.'
          }
          confirmLabel={confirm.status === 'active' ? 'Остановить' : 'Удалить'}
          busy={deleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default PromotionManagement;
